import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject
} from '@angular/core';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { saveAs } from 'file-saver';
import { from } from 'rxjs';
import {
  Address,
  CompanyVendor,
  Contact,
  Contract,
  ExemptVendor,
  File,
  Site,
  Vendor
} from 'src/app/classes/models';
import { LocalizationInfo } from 'src/app/interfaces/localization-info';
import { AuthService } from 'src/app/services/auth.service';
import { CompanyVendorService } from 'src/app/services/company-vendor.service';
import { CompanyService } from 'src/app/services/company.service';
import { ContractService } from 'src/app/services/contract.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';
import { companiesService } from 'src/app/shared/vive-api/companies.service';

@Component({
  selector: 'app-contract-details',
  templateUrl: './contract-details.component.html',
  styleUrls: ['./contract-details.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ContractDetailsComponent {
  ContractId: number | null = null;

  Vendor: Vendor | ExemptVendor | null = null;
  Contract: Contract | null = null;
  Files: File[] | null = [];
  Address: Contact | Address | null = null;
  Site?: Site;
  SiteContact?: Contact;
  VendorContact?: Contact;

  overallVendorRating: number | null = null;
  primaryContact: Contact | null = null;
  contractDetails: Map<string, number | string | Date> = new Map();
  localizationInfo?: LocalizationInfo;
  contractFrequencies!: { id: number; periodName: string }[];

  private _cdr = inject(ChangeDetectorRef);

  constructor(
    private Route: ActivatedRoute,
    private ContractService: ContractService,
    private CompanyVendorService: CompanyVendorService,
    private ToastService: ToastService,
    private UtilityService: UtilityService,
    private CompanyService: CompanyService,
    private AuthService: AuthService
  ) {
    const paramMap: ParamMap = this.Route.snapshot.paramMap;
    const ContractId = Number(paramMap.get('ContractId')) || null;

    if (ContractId) {
      this.ContractId = ContractId;
    }

    const contractQueryObj = {
      id: this.ContractId,
      SiteContact: JSON.stringify({
        StreetAddress: {}
      }),
      VendorContact: JSON.stringify({
        StreetAddress: {}
      }),
      Vendor: JSON.stringify({}),
      ClientRegisteredVendor: JSON.stringify({}),
      ExemptVendor: JSON.stringify({}),
      Site: JSON.stringify({ attributes: ['legalName'] }),
      File: JSON.stringify({}),
      FrequencyPeriod: JSON.stringify({})
    };

    this.ContractService.getContractFrequencies().subscribe({
      next: (frequencies) => {
        if (!frequencies || frequencies.length === 0) {
          throw this.ToastService.addErrorToast(
            'Error',
            'No Contract Frequencies Found.',
            { returnError: true }
          );
        }

        this.contractFrequencies = frequencies;
        this._cdr.markForCheck();
      }
    });

    from(
      companiesService.findOneById({
        pathParams: { companyId: this.AuthService.getUserCompanyId() }
      })
    ).subscribe({
      next: (Company) => {
        if (!Company) {
          throw this.ToastService.addErrorToast(
            'Error',
            'No Company Record Found.',
            { returnError: true }
          );
        }

        const { country } = Company;

        if (country) {
          this.localizationInfo =
            this.UtilityService.getLocalizedValuesMap(country);
        }
        this._cdr.markForCheck();
      }
    });

    this.ContractService.getContract(contractQueryObj).subscribe({
      next: (Contract: Contract) => {
        if (!Contract) {
          throw this.ToastService.addErrorToast('Error', 'No Contract Found!', {
            returnError: true
          });
        }

        this.Contract = Contract;

        const companyVendorQueryObj = {
          attributes: JSON.stringify(['overallVendorRating'])
        };

        const {
          Vendor,
          ExemptVendor,
          ClientRegisteredVendor,
          SiteContact,
          VendorContact,
          Sites,
          Files
        } = Contract;

        if (Sites && Sites.length > 0) {
          const site = Sites[0];
          this.Site = site;
        }

        if (SiteContact) {
          this.SiteContact = SiteContact;
        }

        if (VendorContact) {
          this.VendorContact = VendorContact;
        }

        if (Files && Files.length > 0) {
          this.Files = Files;
        }

        if (Vendor) {
          this.Vendor = Vendor;

          Object.assign(companyVendorQueryObj, { VendorId: Vendor.id });
        }

        if (ExemptVendor) {
          this.Vendor = ExemptVendor;

          Object.assign(companyVendorQueryObj, {
            ExemptVendorId: ExemptVendor.id
          });
        }

        if (ClientRegisteredVendor) {
          this.Vendor = ClientRegisteredVendor;

          Object.assign(companyVendorQueryObj, {
            ClientRegisteredVendorId: ClientRegisteredVendor.id
          });
        }

        if (!this.Vendor) {
          throw this.ToastService.addErrorToast(
            'Error',
            'No Vendor found on contract',
            { returnError: true }
          );
        }

        this.CompanyVendorService.getCompanyVendor(
          companyVendorQueryObj
        ).subscribe({
          next: (CompanyVendor: CompanyVendor) => {
            if (!CompanyVendor) {
              throw this.ToastService.addErrorToast(
                'Error',
                'No CompanyVendor record found',
                { returnError: true }
              );
            }

            const { overallVendorRating } = CompanyVendor;

            this.overallVendorRating = overallVendorRating
              ? Number(overallVendorRating)
              : null;
            this._cdr.markForCheck();
          }
        });
        this._cdr.markForCheck();
      }
    });
  }

  downloadContractDocument(FileId: number) {
    if (!this.ContractId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot download document. No Contract ID found.',
        { returnError: true }
      );
    }
    this.ContractService.getContract(
      this.UtilityService.stringifyObjectsInQueryObject({
        id: this.ContractId,
        File: {
          id: FileId
        }
      })
    ).subscribe({
      next: (Contract: Contract) => {
        if (!Contract) {
          throw this.ToastService.addErrorToast(
            'Error',
            'No Contract Found during download attempt.',
            { returnError: true }
          );
        }

        const { id: ContractId, Files } = Contract;

        if (!ContractId) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing ContractId.',
            { returnError: true }
          );
        }

        if (!Files || Files.length === 0) {
          throw this.ToastService.addErrorToast(
            'Error',
            'No Files Found for Download.',
            { returnError: true }
          );
        }

        const contractDocument: File = Files[0];
        const { id: FileId, filepath } = contractDocument;

        if (!FileId) {
          throw this.ToastService.addErrorToast('Error', 'Missing FileId', {
            returnError: true
          });
        }

        if (!filepath) {
          throw this.ToastService.addErrorToast(
            'Error',
            'No file path found.',
            { returnError: true }
          );
        }

        this.ContractService.getContractFileS3PresignedUrl(
          ContractId,
          FileId
        ).subscribe({
          next: ({ presignedUrl, filename }) => {
            saveAs(presignedUrl, filename);
          }
        });
      }
    });
  }
}
